import { supabase } from "@/integrations/supabase/client";

export interface SavedJob {
  id: string;
  user_id: string;
  job_title: string;
  company_name: string;
  job_url?: string | null;
  job_description_text?: string | null;
  source?: string | null;
  application_status: string;
  reports_generated: any[];
  created_at: string;
  updated_at: string;
}

export async function findOrCreateSavedJob(
  userId: string,
  jobTitle: string,
  companyName: string,
  jobUrl?: string,
  jobDescriptionText?: string,
  source?: string
): Promise<SavedJob> {
  // Try to find existing
  const { data: existing, error: findError } = await supabase
    .from('jobintel_saved_jobs' as any)
    .select('*')
    .eq('user_id', userId)
    .eq('company_name', companyName)
    .eq('job_title', jobTitle)
    .maybeSingle();

  if (findError) throw findError;
  if (existing) return existing as unknown as SavedJob;

  // Create new
  const { data: created, error: createError } = await supabase
    .from('jobintel_saved_jobs' as any)
    .insert({
      user_id: userId,
      job_title: jobTitle,
      company_name: companyName,
      job_url: jobUrl || null,
      job_description_text: jobDescriptionText || null,
      source: source || 'tool_auto_save',
    } as any)
    .select()
    .single();

  if (createError) throw createError;
  return created as unknown as SavedJob;
}

export async function linkReportToSavedJob(
  savedJobId: string,
  reportType: string,
  reportVersion: string,
  reportId: string
): Promise<void> {
  // Fetch current reports_generated
  const { data, error: fetchError } = await supabase
    .from('jobintel_saved_jobs' as any)
    .select('reports_generated')
    .eq('id', savedJobId)
    .single();

  if (fetchError) throw fetchError;

  const currentReports = ((data as any)?.reports_generated as any[]) || [];
  const updatedReports = [
    ...currentReports,
    {
      report_type: reportType,
      report_version: reportVersion,
      report_id: reportId,
      generated_at: new Date().toISOString(),
    },
  ];

  const { error: updateError } = await supabase
    .from('jobintel_saved_jobs' as any)
    .update({ reports_generated: updatedReports } as any)
    .eq('id', savedJobId);

  if (updateError) throw updateError;
}

export async function updateApplicationStatus(
  savedJobId: string,
  status: string
): Promise<void> {
  const { error } = await supabase
    .from('jobintel_saved_jobs' as any)
    .update({ application_status: status } as any)
    .eq('id', savedJobId);

  if (error) throw error;
}
